import { Form, Modal } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../contexts/ToastContext";
import {
    createCustomerForInsurance,
    getInsuranceCustomerUserId,
    mapInsuranceCustomerFormValues,
    updateCustomerForInsurance,
} from "../../lib/insuranceSaleApi";
import useUsStatesWithCoverage from "../../hooks/useUsStatesWithCoverage";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import InsuranceCustomerFormFields from "./InsuranceCustomerFormFields";

const BASE_FIELDS = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "dateOfBirth",
    "licenseNumber",
    "licenseState",
    "licenseExpiration",
];

const BONZAH_FIELDS = ["addressLine1", "zipCode"];

const InsuranceCustomerModal = ({
    open,
    mode,
    provider,
    initialUser,
    onClose,
    onSuccess,
    translationNamespace,
}) => {
    const { t } = useTranslation(translationNamespace);
    const toast = useToast();
    const [form] = Form.useForm();
    const { states, loading: statesLoading } = useUsStatesWithCoverage();
    const [submitLoading, setSubmitLoading] = useState(false);

    const isBonzah = String(provider).toLowerCase() === "bonzah";
    const isEdit = mode === "edit";

    useEffect(() => {
        if (!open) {
            return;
        }

        form.resetFields();

        if (isEdit && initialUser) {
            form.setFieldsValue(mapInsuranceCustomerFormValues(initialUser, states));
        }
    }, [open, isEdit, initialUser, form, states]);

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    const handleSubmit = async () => {
        try {
            const fields = isBonzah ? [...BASE_FIELDS, ...BONZAH_FIELDS] : BASE_FIELDS;
            const values = await form.validateFields(fields);
            setSubmitLoading(true);

            const userId = getInsuranceCustomerUserId(initialUser);
            if (isEdit && !userId) {
                throw new Error("Customer ID is missing");
            }

            const savedUser = isEdit
                ? await updateCustomerForInsurance(userId, values)
                : await createCustomerForInsurance(values);

            toast.success({
                message: t("wizard.addCustomerSuccess"),
            });
            form.resetFields();
            onSuccess?.(savedUser);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }

            toast.error({
                message: t("wizard.addCustomerError"),
                description: error?.response?.data?.message || error?.message,
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            title={isEdit ? t("wizard.editCustomerTitle") : t("wizard.addCustomerTitle")}
            onCancel={handleCancel}
            onOk={handleSubmit}
            okText={t("wizard.saveCustomer")}
            cancelText={t("wizard.cancel")}
            confirmLoading={submitLoading}
            centered
            width={{
                xs: "calc(100vw - 2rem)",
                sm: 640,
            }}
            destroyOnClose
            maskClosable={false}
            styles={{
                body: { maxHeight: "min(80vh, 720px)", overflowY: "auto" },
            }}>
            <Form form={form} layout="vertical" requiredMark={customizeRequiredMark} className="mt-4">
                {isBonzah ? (
                    <p className="mb-4 text-sm text-gray-600">{t("wizard.bonzahCustomerHint")}</p>
                ) : null}

                <InsuranceCustomerFormFields
                    t={t}
                    provider={provider}
                    states={states}
                    statesLoading={statesLoading}
                />
            </Form>
        </Modal>
    );
};

InsuranceCustomerModal.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(["create", "edit"]).isRequired,
    provider: PropTypes.string,
    initialUser: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    translationNamespace: PropTypes.string,
};

InsuranceCustomerModal.defaultProps = {
    provider: undefined,
    initialUser: null,
    onSuccess: null,
    translationNamespace: "sellInsurance",
};

export default InsuranceCustomerModal;
